import { useEffect, useMemo, useCallback, useRef, Dispatch, SetStateAction } from 'react';
import { useStore } from 'zustand';
import type { FormTreeStore, FormUtils, FormState, FormToken, FormNode, FormTreeStoreInstance, ZExtensionProtocol } from './types';
import { defaultFormStore } from './store';
import { generateLensId } from './lens';
import { useZTreeStoreContext } from './store.context';


/**
 * Hook for creating a Zustand-powered form slice
 */
export function useZSlice<S, P = S>(
  store: FormTreeStoreInstance,
  formToken: FormToken,
  initialData: () => FormState<S, P>,
): FormUtils<S, P> {
  const storeInstance = useMemo(() => store || defaultFormStore, [store]);
  const initialFnRef = useRef(initialData)
  initialFnRef.current = initialData

  // Register node on mount
  useEffect(() => {
    const nodeId = formToken.id;
    const state = initialFnRef.current();
    storeInstance.getState().register(formToken, state);
    // Cleanup on unmount
    return () => {
      storeInstance.getState().unregister(nodeId);
      // registerStubRef.current = false
    };
  }, [formToken, storeInstance]);

  // Select form state from store
  const formState = useStore(
    storeInstance,
    useCallback(
      (state) => {
        const node = state.getNode(formToken.id);
        return node?.state ?? initialFnRef.current();
      },
      [formToken.id]
    )
  );

  const setStates : Dispatch<SetStateAction<FormState<S, P>>> = useCallback(
    (updatr: FormState<S, P>|((states_0: FormState<S, P>) => FormState<S, P>)) => {
      storeInstance.getState().updateNode<S, P>(formToken.id, (set) => { set(updatr) })
    }, 
    [formToken.id, storeInstance])

  const actionAsync = useCallback(
    (
      updateOrchestrator: (
        nodeSet: (
          newState: | FormState<S, P>
                    | ((v0: FormState<S, P>)=>FormState<S, P>)
        ) => void,
        nodeGet: () => FormState<S, P>
      ) => void | Promise<void>
    ) => {
      storeInstance.getState().updateNode<S, P>(formToken.id, updateOrchestrator)
    }, 
    [formToken.id, storeInstance])

  const installProtocolMetadata = useCallback(
    (protocol: ZExtensionProtocol|string, metadata: any) => {
      const p : ZExtensionProtocol = typeof protocol === 'string' ? { metadataKey: protocol } as any : protocol
      storeInstance.getState().installNodeProtocolMetadata(formToken.id, p, metadata)
    },
    [formToken.id, storeInstance]);

  const invokeProtocol: (protocol: ZExtensionProtocol) => Promise<void> = useCallback(
    (protocol) => {
      return storeInstance.getState().invokeNodeProtocol(formToken.id, protocol)
    },
    [formToken.id, storeInstance])

  // useRef instead of useMemo: have to fight React Strict Mode...
  const hooksRef = useRef({} as { 
    formTokenId: string, 
    store: FormTreeStoreInstance,  
    hooks: {
      useZSelector: <T>(selector: (node: FormNode<S, P>, store: FormTreeStore) => T) => T | null;
      useZHasDescendantChanges: () => boolean;
      useZHasDescendantError: () => boolean;
    }  
  })
  if (hooksRef.current.formTokenId !== formToken.id || hooksRef.current.store !== store) {
    const bag = hooksRef.current
    bag.formTokenId = formToken.id
    bag.store = store

    /**
     * Hook for selective re-renders based on form state selectors
     */
    function useZSelector<T>(
      selector: (
        node: FormNode<S, P>,
        store: FormTreeStore
      ) => T
    ): T|null {  
      return useStore(
        store,
        (state) => {
          const node = state.getNode(formToken.id);
          return node ? selector(node, state) : null
        }
      );
    }
  
    /**
     * Hook to check if any of its descendants has changes
     */
    function useZHasDescendantChanges(): boolean {
      return !!useZSelector((node, store) => {
        let hasChanges = false
        store.traverseDescendants(formToken.id, (next) => {
          let childNode : FormNode|null = null 
          while (!hasChanges && (childNode = next())) {
            hasChanges = Object.values(childNode.state.touched).some(x => x)
          }
        })
        return hasChanges
      })
    }
  
    /**
     * Hook to check if any of its descendants has errors
     */
    function useZHasDescendantError(): boolean {
      return !!useZSelector((node, store) => {
        let hasError = false
        store.traverseDescendants(formToken.id, (next) => {
          let childNode : FormNode|null = null 
          while (!hasError && (childNode = next())) {
            hasError = Object.values(childNode.state.errors).some(x => !!x)
          }
        })
        return hasError
      })
    }
  
    bag.hooks = {
      useZSelector,
      useZHasDescendantChanges,
      useZHasDescendantError
    }
  }
  const hooks = hooksRef.current.hooks

  // Compute derived values
  const utils : FormUtils<S, P> = {
    states: formState,
    setStates,
    actionAsync,
    installProtocolMetadata,
    invokeProtocol,
    hooks,
    token: formToken,
  }
  return utils
}


export const useZSliceUnderContext = <S, P = S, E = object>(x: {
  parentToken?: FormToken|null,
  pathFragment?: string,
  /** Original data before any modifications; should ensure stability */
  prevData?: P,
  stageInit: () => S,
  extraStates?: E|((x?:P)=>E),
  store?: FormTreeStoreInstance,
}) : FormUtils<S, P, E>&{resetStates: ()=>void;} => {
  const {
    parentToken,
    pathFragment,
    prevData,
    stageInit,
    extraStates,
    store: storeProps
  } = x
  const bagRef = useRef({ parentToken, pathFragment })
  if (parentToken && !pathFragment) {
    throw new Error('Argument "pathFragment" is required when "parentToken" is provided')
  }
  const store = useZTreeStoreContext()
  const storeFinal = storeProps ?? store
  const selfToken: FormToken = useMemo(() => {
    const { parentToken, pathFragment } = bagRef.current
    return !parentToken
      ? { id: 'root', path: '', parentId: null }
      : { id: generateLensId(), path: `${parentToken.path}.${pathFragment}`, parentId: parentToken.id }
  }, [])
  const initialDataFnRef = useRef(() => ({}) as any)
  initialDataFnRef.current = () => ({
    prevData: prevData ?? emptyPrevData,
    stage: stageInit(),
    errors: {},
    touched: {},
    ...(!extraStates ? null
      : extraStates instanceof Function ? extraStates(prevData)
      : extraStates
    )
  })

  const utils = useZSlice<S, P>(
    storeFinal,
    selfToken,
    () => ({
      ...initialDataFnRef.current(),
      metadata: {},
    })
  )

  // reset states when prevData updates...
  const { states, setStates } = utils
  const resetStates = useCallback(() => {
    setStates(v => ({...v,
      ...initialDataFnRef.current()
    }))
  }, [setStates])

  useEffect(() => {
    const propPrevData = prevData ?? emptyPrevData
    if (states.prevData !== propPrevData) {
      resetStates()
    }
  }, [prevData, resetStates, states.prevData])

  return ({...(utils as any as FormUtils<S, P, E>), resetStates })
}

const emptyPrevData = Object.freeze({})
